import { useMemo, useRef } from 'react';
import { motion } from 'motion/react';
import { TypedStageEngagement } from '@/utils/engagementTypeUtils';
import {
  RaffleStatus,
  RaffleSubmissionData,
  RaffleViewConfig,
  RaffleViewData,
} from '@/generated/graphql';
import { useStageEngagementSubmissions } from '@/hooks/useStageEngagementSubmissions';
import { RaffleTicket } from '../RaffleTicket';
import { Loader } from '@/components/Loader';
import { getRaffleWinnerIds } from '../winners';
import {
  disposalTypeForSubmissionId,
  raffleDisposalExit,
} from '../drawing/disposal';
import { FlameBurst } from '../drawing/FlameBurst';
import { FlyOffTrail } from '../drawing/FlyOffTrail';
import { GravityShadow } from '../drawing/GravityShadow';
import { WinnerConfetti } from '../drawing/WinnerConfetti';
import { RaffleStatusChip } from '../drawing/RaffleStatusChip';
import { ClaimPrizeBar } from '../drawing/ClaimPrizeBar';
import { EmptySeatGlow } from '../drawing/EmptySeatGlow';
import { visibleRaffleSubmissionIds } from '../drawing/visibleTickets';
import {
  raffleClaimCopy,
  raffleDisplayedRemainingCount,
  raffleTicketFlipDirection,
  raffleTicketStartVisibleSide,
  raffleTicketVisual,
  raffleTicketWiggleVariants,
  raffleWinnerTilt,
  rememberRaffleTicketFlipDirection,
  type RaffleTicketFlipDirection,
} from '../drawing/ticketMotion';
import { useRaffleDrawMotion } from '../drawing/useRaffleDrawMotion';
import { useRaffleTicketWiggle } from '../drawing/useRaffleTicketWiggle';

type Props = {
  engagement: TypedStageEngagement<
    RaffleViewConfig,
    RaffleViewData,
    RaffleSubmissionData
  >;
};

const COLUMN_GAP_PX = 16;
const ROW_GAP_PX = 14;
const TICKET_MAX_WIDTH_PX = 360;

/** 3 columns through 9 tickets; then grow with sqrt so the grid stays roughly square. */
function raffleStageGridColumns(
  ticketCount: number,
  winnersOnlyBoard = false,
): number {
  const count = Math.max(ticketCount, 1);
  if (winnersOnlyBoard && count < 3) {
    return count;
  }
  return Math.max(3, Math.ceil(Math.sqrt(count)));
}

export function StageRaffleEngagement({ engagement }: Props) {
  const { data, loading, error } =
    useStageEngagementSubmissions<RaffleSubmissionData>({
      engagementId: engagement.id,
    });

  const submissions = useMemo(
    () => data?.submissions ?? [],
    [data?.submissions],
  );
  const viewData = engagement.viewData;
  const status = viewData?.raffleStatus;
  const winnerIds = getRaffleWinnerIds(viewData);
  const remainingIds = useMemo(
    () => viewData?.remainingSubmissionIds ?? [],
    [viewData?.remainingSubmissionIds],
  );
  const lastEliminatedId = viewData?.lastEliminatedSubmissionId ?? null;
  const allIds = useMemo(
    () => submissions.map((submission) => submission.id),
    [submissions],
  );

  const motionState = useRaffleDrawMotion({
    status,
    remainingIds,
    lastEliminatedId,
    allIds,
  });
  const wigglingById = useRaffleTicketWiggle(engagement.id);

  const visibleIds = useMemo(
    () =>
      visibleRaffleSubmissionIds({
        status,
        remainingSubmissionIds: remainingIds,
        winnerSubmissionIds: winnerIds,
        allSubmissionIds: allIds,
      }),
    [allIds, remainingIds, status, winnerIds],
  );

  const visibleSubmissions = useMemo(() => {
    const byId = new Map(
      submissions.map((submission) => [submission.id, submission]),
    );
    return visibleIds.flatMap((id) => {
      const submission = byId.get(id);
      return submission ? [submission] : [];
    });
  }, [submissions, visibleIds]);

  const ticketFlipByIdRef = useRef(
    new Map<string, RaffleTicketFlipDirection>(),
  );
  const configuredFlipDirection = raffleTicketFlipDirection(
    engagement.viewConfig,
  );

  const cols = useMemo(
    () =>
      raffleStageGridColumns(
        visibleSubmissions.length,
        status === RaffleStatus.Completed,
      ),
    [status, visibleSubmissions.length],
  );

  const remainingDisplay = raffleDisplayedRemainingCount({
    remainingIds,
    lastEliminatedId,
    motion: motionState,
  });

  if (loading) return <Loader />;
  if (error) return <div>Error: {error.message}</div>;

  const celebrating = status === RaffleStatus.Completed;
  const drawing = status === RaffleStatus.Drawing;
  const ready = status === RaffleStatus.ReadyToDraw;
  const claimCopy = raffleClaimCopy(engagement.viewConfig);
  const showCanvasTitle = !celebrating;

  return (
    <div
      className={
        celebrating
          ? 'relative flex h-full min-h-0 w-full flex-col overflow-hidden'
          : 'relative flex h-full min-h-0 w-full flex-col overflow-visible'
      }
    >
      {showCanvasTitle && (
        <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
          <h1 className="font-screen-body text-[clamp(2.1rem,3.4vw,3.4rem)] leading-none text-white">
            {engagement.title}
          </h1>
          {(ready || status === RaffleStatus.Open) && (
            <RaffleStatusChip tone="muted">
              {submissions.length}{' '}
              {submissions.length === 1 ? 'entry' : 'entries'}
            </RaffleStatusChip>
          )}
          {ready && (
            <RaffleStatusChip tone="orange">Entries closed</RaffleStatusChip>
          )}
          {drawing && (
            <>
              <RaffleStatusChip tone="orange" className="tracking-[0.12em]">
                Drawing…
              </RaffleStatusChip>
              <RaffleStatusChip tone="muted">
                {remainingDisplay} remaining
              </RaffleStatusChip>
            </>
          )}
        </div>
      )}

      {celebrating && (
        <div className="mb-6 shrink-0 text-center" data-name="RAFFLE_CONGRATS">
          <h2 className="font-screen-body text-[clamp(2.75rem,4.2vw,4rem)] text-white">
            Congrats!
          </h2>
          <p className="mt-1 font-screen-body text-sm uppercase tracking-[0.22em] text-white/45">
            {winnerIds.length} {winnerIds.length === 1 ? 'winner' : 'winners'}
          </p>
        </div>
      )}

      <div
        className={
          celebrating
            ? 'min-h-0 flex-1 overflow-y-auto'
            : 'min-h-0 flex-1 overflow-visible'
        }
      >
        <motion.ul
          initial={false}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, minmax(0, ${TICKET_MAX_WIDTH_PX}px))`,
            columnGap: COLUMN_GAP_PX,
            rowGap: celebrating ? 36 : ROW_GAP_PX,
            alignContent: 'start',
            alignItems: 'start',
            justifyContent: celebrating ? 'center' : 'start',
            width: '100%',
            listStyle: 'none',
            padding: celebrating ? '12px 0 0' : 0,
            margin: 0,
            overflow: 'visible',
          }}
        >
          {visibleSubmissions.map((submission, i) => {
            const visual = raffleTicketVisual({
              status,
              submissionId: submission.id,
              remainingIds,
              winnerIds,
              motion: motionState,
            });
            const disposalType = disposalTypeForSubmissionId(submission.id);
            const flipDirection = rememberRaffleTicketFlipDirection(
              ticketFlipByIdRef.current,
              submission.id,
              configuredFlipDirection,
            );
            const visibleSide = raffleTicketStartVisibleSide(flipDirection);
            const isEmpty = visual === 'empty';
            const disposing = visual === 'disposing';
            const selected = visual === 'selected';
            const isWinner = visual === 'winner';
            const showGlow = selected || disposing || isEmpty;
            const shouldWiggle =
              !disposing &&
              !isEmpty &&
              status === RaffleStatus.Open &&
              Boolean(wigglingById[submission.id]);

            return (
              <li
                key={submission.id}
                data-name="RAFFLE_STAGE_CELL"
                data-visual={visual}
                data-flip-direction={flipDirection}
                style={{
                  minWidth: 0,
                  maxWidth: TICKET_MAX_WIDTH_PX,
                  position: 'relative',
                  overflow: 'visible',
                  paddingTop: isWinner ? 18 : 0,
                }}
              >
                <EmptySeatGlow visible={showGlow} />
                <motion.div
                  className="relative overflow-visible"
                  initial="rest"
                  animate={disposing ? 'dispose' : 'rest'}
                  variants={{
                    rest: {
                      opacity: 1,
                      scale: 1,
                      x: 0,
                      y: 0,
                      rotate: 0,
                      filter: 'none',
                    },
                    dispose: raffleDisposalExit(submission.id),
                  }}
                  style={{
                    visibility: isEmpty ? 'hidden' : 'visible',
                  }}
                >
                  {disposing && disposalType === 'fly-off' && (
                    <FlyOffTrail submissionId={submission.id} />
                  )}
                  {disposing && disposalType === 'gravity' && <GravityShadow />}
                  <motion.div
                    className="relative overflow-visible"
                    data-name="RAFFLE_STAGE_TICKET_WIGGLE"
                    data-wiggle={shouldWiggle ? 'true' : 'false'}
                    initial="rest"
                    animate={shouldWiggle ? 'wiggle' : 'rest'}
                    variants={raffleTicketWiggleVariants}
                  >
                    <RaffleTicket
                      visibleSide={visibleSide}
                      delayedFlip={1_000 + i * 15}
                      signatureImageUrl={submission.data?.optionalImageUrl}
                      isWinner={isWinner}
                      selected={selected}
                      dimmed={false}
                      showWinnerBadge={isWinner}
                      winnerTilt={
                        isWinner ? raffleWinnerTilt(submission.id) : 0
                      }
                      className="w-full"
                    />
                  </motion.div>
                  {disposing && disposalType === 'flames' && <FlameBurst />}
                  <WinnerConfetti
                    submissionId={submission.id}
                    active={isWinner}
                  />
                </motion.div>
                {isWinner && submission.data?.name && (
                  <p className="mt-2 text-center font-screen-body text-sm text-white/80">
                    {submission.data.name}
                  </p>
                )}
              </li>
            );
          })}
        </motion.ul>
      </div>

      {celebrating && (
        <div className="shrink-0 pb-2 pt-5">
          <ClaimPrizeBar copy={claimCopy} />
        </div>
      )}
    </div>
  );
}
